import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { PersonaBadge } from "../components/Badges";
import { ClientInfo } from "../components/client/ClientInfo";
import { LoanCard } from "../components/client/LoanCard";
import { DriveFolderView } from "../components/client/DriveFolderView";
import { ClientDownloads } from "../components/client/ClientDownloads";
import { AdminClientActions } from "../components/client/AdminClientActions";
import { api, errorMessage } from "../lib/api";
import { toast } from "sonner";
import { canWrite, isAdmin, useAuth } from "../context/AuthContext";

const TipoControl = ({ client, onPersona, error }) => (
  <span className="inline-flex flex-col">
    <select value={client.persona} onChange={(ev) => onPersona(ev.target.value)} className="tr-input h-8 w-auto py-0 text-xs font-semibold" data-testid="client-tipo-select" aria-label="Tipo de persona">
      <option value="PF">Persona física</option>
      <option value="PM">Persona moral</option>
    </select>
  </span>
);

const ClientHeader = ({ client, onBack, canEdit, onPersona, tipoError }) => {
  return (
    <section className="tr-fade-up space-y-4">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f5ef7] hover:text-[#0a47be]" data-testid="back-to-dashboard">
        <ArrowLeft className="h-4 w-4" /> Volver al panel
      </button>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <div className="tr-eyebrow">Ficha del cliente</div>
          <h1 className="break-words text-2xl font-extrabold tracking-tight text-[#182235] sm:text-3xl" data-testid="client-name">{client.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-white px-3 py-1 font-mono text-xs font-bold text-[#182235] ring-1 ring-[#d7e0ec]" data-testid="client-borrower-id">{client.borrower_id}</span>
          {canEdit ? <TipoControl client={client} onPersona={onPersona} /> : <PersonaBadge persona={client.persona} label={client.persona_label} testId="client-tipo" />}
          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#3b475c] ring-1 ring-[#d7e0ec]" data-testid="client-loan-count">
            {client.loans.length} {client.loans.length === 1 ? "crédito" : "créditos"}
          </span>
        </div>
      </div>
      <ClientDownloads client={client} />
      {tipoError && (
        <div role="alert" className="flex items-start gap-2 rounded-[14px] border border-[#f3c9c9] bg-[#fdf0ef] px-4 py-3 text-sm font-semibold text-[#b42318]" data-testid="client-tipo-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {tipoError}
        </div>
      )}
      {client.origin === "loans_only" && !client.in_borrower_sheet && (
        <div className="flex items-start gap-2 rounded-[14px] border border-[#f3d695] bg-[#fff7e8] px-4 py-3 text-sm text-[#7a5b10]" data-testid="client-missing-notice">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Este cliente no aparece en la hoja de clientes del archivo; se creó a partir de sus créditos.
        </div>
      )}
    </section>
  );
};

export default function ClientPage() {
  const { borrowerId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tipoError, setTipoError] = useState("");

  const load = () =>
    api
      .get(`/clients/${encodeURIComponent(borrowerId)}`)
      .then((r) => setClient(r.data))
      .catch((err) => err.response?.status === 404 && setNotFound(true));

  useEffect(() => {
    setClient(null);
    setTipoError("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowerId]);

  const onPersona = async (persona) => {
    setTipoError("");
    try {
      await api.put(`/clients/${encodeURIComponent(borrowerId)}/persona`, { persona });
      toast.success("Tipo actualizado");
      load();
    } catch (err) {
      const m = errorMessage(err, "No se pudo cambiar el tipo, intenta de nuevo");
      setTipoError(m);
      toast.error(m);
    }
  };

  const onBack = () => navigate("/");

  if (notFound)
    return (
      <AppShell>
        <div className="tr-card p-8 text-center" data-testid="client-not-found">
          <p className="font-bold">Cliente no encontrado</p>
          <button type="button" onClick={() => navigate("/")} className="tr-btn-primary mt-4 px-4 py-2 text-sm" data-testid="not-found-back">Volver al panel</button>
        </div>
      </AppShell>
    );

  if (!client)
    return (
      <AppShell>
        <div className="flex justify-center py-20" data-testid="client-loading"><Loader2 className="h-6 w-6 animate-spin text-[#0f5ef7]" /></div>
      </AppShell>
    );

  return (
    <AppShell>
      <ClientHeader client={client} onBack={onBack} canEdit={canWrite(user)} onPersona={onPersona} tipoError={tipoError} />
      {isAdmin(user) && <AdminClientActions client={client} />}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8">
          <DriveFolderView clientName={client.name} files={client.expediente.files || []} />
        </div>
        <div className="min-w-0 lg:col-span-4">
          <ClientInfo client={client} />
        </div>
      </div>
      <section className="space-y-4" data-testid="loans-section">
        <div>
          <div className="tr-eyebrow">Créditos</div>
          <h2 className="text-base font-bold text-[#182235] md:text-lg">Créditos del cliente ({client.loans.length})</h2>
        </div>
        {client.loans.length === 0 && (
          <div className="tr-card p-6 text-sm font-semibold text-[#5b667a]" data-testid="loans-empty">Sin datos de crédito</div>
        )}
        {client.loans.map((l) => <LoanCard key={l.loan_id} loan={l} />)}
      </section>
    </AppShell>
  );
}
